import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import toast from 'react-hot-toast';
import './ManageAccountModal.css';

function ManageAccountModal({ isOpen, onClose, user }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchUserData();
    }
  }, [isOpen, user]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        setUserData(data);
      } else {
        setUserData({});
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserData({});
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        formData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, formData.newPassword);

      toast.success('Password updated successfully');
      setSuccess(true);
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error updating password:', error);
      let errorMessage = 'Failed to update password. Please try again.';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect';
        setErrors({ currentPassword: errorMessage });
        toast.error(errorMessage);
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
        setErrors({ newPassword: errorMessage });
        toast.error(errorMessage);
      } else {
        const finalError = error.message || errorMessage;
        setErrors({ submit: finalError });
        toast.error(finalError);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="manage-account-modal-overlay" onClick={handleClose}>
      <div className="manage-account-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Manage Account</h2>
          <button className="close-button" onClick={handleClose} title="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading account information...</p>
            </div>
          ) : (
            <>
              <div className="account-info-section">
                <div className="info-item">
                  <label>Full Name</label>
                  <div className="info-value">{userData?.name || 'Not set'}</div>
                </div>
                <div className="info-item">
                  <label>Email</label>
                  <div className="info-value">{user?.email || 'Not set'}</div>
                </div>
                {userData?.pemail && (
                  <div className="info-item">
                    <label>Additional Email</label>
                    <div className="info-value">{userData.pemail}</div>
                  </div>
                )}
              </div>

              <div className="divider"></div>

              <form className="password-form" onSubmit={handleSubmit}>
                <h3>Change Password</h3>
                
                {success && (
                  <div className="success-message">
                    <i className="fas fa-check-circle"></i>
                    <span>Password updated successfully!</span>
                  </div>
                )}

                {errors.submit && (
                  <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{errors.submit}</span>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="current-password">
                    Current Password <span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    id="current-password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    className={errors.currentPassword ? 'error' : ''}
                    placeholder="Enter your current password"
                    disabled={saving}
                  />
                  {errors.currentPassword && (
                    <span className="field-error">{errors.currentPassword}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="new-password">
                    New Password <span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    id="new-password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    className={errors.newPassword ? 'error' : ''}
                    placeholder="Enter your new password"
                    disabled={saving}
                  />
                  {errors.newPassword && (
                    <span className="field-error">{errors.newPassword}</span>
                  )}
                  <div className="password-hint">
                    Password must be at least 6 characters long
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirm-password">
                    Confirm New Password <span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    id="confirm-password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={errors.confirmPassword ? 'error' : ''}
                    placeholder="Confirm your new password"
                    disabled={saving}
                  />
                  {errors.confirmPassword && (
                    <span className="field-error">{errors.confirmPassword}</span>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleClose}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-key"></i>
                        Update Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ManageAccountModal;
